'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Script from 'next/script';
import { useRouter } from 'next/navigation';
import { ArrowRight, CheckCircle2, Globe, FileText, ShieldCheck, BookOpen, Trophy, Star, ChevronRight, Menu, X, Zap, TrendingUp, BadgeCheck, Gift, Phone, Mail, MapPin } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';

const CURRENT_YEAR = 2026;

const STATS = [
{ value: '2,400+', label: 'Active Agents' },
{ value: '18,000+', label: 'Orders Delivered' },
{ value: '₹4.2Cr+', label: 'Agent Earnings' },
{ value: '99.1%', label: 'Satisfaction Rate' }];


const SERVICES = [
{
  icon: Globe,
  title: 'Website Development',
  desc: 'Sell professional websites to local businesses — restaurants, clinics, shops. We build it within 24 hours, you earn the commission.',
  tag: 'Most Popular',
  color: 'from-[#0f172a] to-[#1e3a8a]',
  textColor: 'text-white',
  descColor: 'text-blue-200',
  tagColor: 'bg-white/10 text-white',
  iconBg: 'bg-white/10',
  iconColor: 'text-white',
  span: 'lg:col-span-2'
},
{
  icon: FileText,
  title: 'Press Release Services',
  desc: 'Get your clients featured in top news portals. We write and distribute, you deliver the results.',
  tag: 'High Demand',
  color: 'from-amber-50 to-orange-50',
  textColor: 'text-[#0f172a]',
  descColor: 'text-slate-500',
  tagColor: 'bg-amber-100 text-amber-700',
  iconBg: 'bg-amber-100',
  iconColor: 'text-amber-600',
  span: ''
},
{
  icon: ShieldCheck,
  title: 'Business Compliance',
  desc: 'GST registration, MSME, trademark, and legal filings. Help businesses stay compliant — we handle all paperwork.',
  tag: 'Recurring Revenue',
  color: 'from-emerald-50 to-teal-50',
  textColor: 'text-[#0f172a]',
  descColor: 'text-slate-500',
  tagColor: 'bg-emerald-100 text-emerald-700',
  iconBg: 'bg-emerald-100',
  iconColor: 'text-emerald-600',
  span: ''
}];


const BENEFITS = [
{
  icon: BookOpen,
  title: 'Training Inside',
  desc: 'Step-by-step video training on how to find clients, pitch services, and close deals. No prior experience needed.',
  color: 'bg-blue-50',
  iconColor: 'text-[#1a56ff]'
},
{
  icon: Gift,
  title: 'Refer & Earn 15%',
  desc: 'Refer other agents and earn 15% commission on every wallet top-up they make — passive income on autopilot.',
  color: 'bg-rose-50',
  iconColor: 'text-rose-600'
},
{
  icon: Zap,
  title: 'Ready in 24 Hours',
  desc: 'Every order is fulfilled within 24 hours. Deliver fast, build reputation, earn repeat business.',
  color: 'bg-amber-50',
  iconColor: 'text-amber-600'
}];


const HOW_IT_WORKS = [
{
  step: '01',
  title: 'Create Your Account',
  desc: 'Register as an agent with a one-time activation fee. Get lifetime access to all services, training, and your agent dashboard.'
},
{
  step: '02',
  title: 'Choose Your Service',
  desc: 'Pick from Website Development, Press Release, or Business Compliance — or sell all three.'
},
{
  step: '03',
  title: 'Find a Client & Place Order',
  desc: 'Approach local businesses. Once they agree, place the order on your dashboard in under 2 minutes.'
},
{
  step: '04',
  title: 'We Deliver, You Earn',
  desc: 'Our expert team delivers within 24 hours. Commission hits your wallet automatically.'
}];


const TESTIMONIALS = [
{
  name: 'Rahul Sharma',
  city: 'Pune',
  text: 'I closed 3 website deals in my first month. The backend team delivers fast and the clients are always happy.',
  orders: 47,
  initials: 'RS',
  color: 'bg-blue-100 text-blue-700'
},
{
  name: 'Priya Nair',
  city: 'Kochi',
  text: 'No technical skills needed at all. I just talk to clients and dropservice.in handles everything from building to delivery.',
  orders: 31,
  initials: 'PN',
  color: 'bg-rose-100 text-rose-700'
},
{
  name: 'Amit Verma',
  city: 'Jaipur',
  text: 'The authorization certificate gave me instant credibility. Clients trust me because I represent a real company.',
  orders: 89,
  initials: 'AV',
  color: 'bg-emerald-100 text-emerald-700'
}];


const FOOTER_LINKS = {
  Company: [
  { label: 'About dropservice.in', href: '/about' },
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Agent Leaderboard', href: '/leaderboard' },
  { label: 'Careers', href: '/careers' }],

  Services: [
  { label: 'Website Development', href: '/#services' },
  { label: 'Press Release', href: '/#services' },
  { label: 'Business Compliance', href: '/#services' },
  { label: 'Refer & Earn', href: '/affiliate' }],

  Support: [
  { label: 'Help Centre', href: '/help-centre' },
  { label: 'Contact Us', href: '/contact' },
  { label: "FAQ\'s", href: '/faqs' },
  { label: 'Report an Issue', href: '/report-issue' }],

  Legal: [
  { label: 'Terms & Conditions', href: '/terms' },
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Refund Policy', href: '/refund-policy' },
  { label: 'Cookie Policy', href: '/cookie-policy' }]

};

const FAQS = [
{
  q: 'Do I need technical skills to become an agent?',
  a: 'No. You only need to find clients and place orders. Our expert team handles all technical work — website building, press releases, and compliance filings.'
},
{
  q: 'What is the one-time registration fee for?',
  a: 'It is a one-time lifetime access fee. You get your agent dashboard, authorization certificate, free training videos, and access to all services — forever.'
},
{
  q: 'How fast are orders delivered?',
  a: 'Most orders are delivered within 24 hours. Complex projects may take up to 48–72 hours. You will be notified at every step.'
},
{
  q: 'How does the Refer & Earn program work?',
  a: 'Share your unique referral link. When someone registers using your link, you earn ₹750 as a joining bonus. You also earn 15% commission every time your referrals add funds to their wallet — credited directly to your wallet.'
},
{
  q: 'What is the Authorization Certificate?',
  a: 'It is an official certificate from dropservice.in that you can show to clients to establish credibility and trust as an authorized agent.'
}];


export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const router = useRouter();
  const { user: authUser, loading: authLoading } = useAuth();
  const isLoggedIn = !authLoading && !!authUser;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-[#0f172a]">
      {/* ── NAV ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'}`
        }>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <span className="text-[17px] font-bold tracking-tight select-none">
            <span className="text-[#0f172a]">dropservice</span>
            <span className="text-[#1a56ff]">.in</span>
          </span>

          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
            <a href="#services" className="hover:text-[#1a56ff] transition-colors">Services</a>
            <a href="#how-it-works" className="hover:text-[#1a56ff] transition-colors">How it works</a>
            <a href="#faq" className="hover:text-[#1a56ff] transition-colors">FAQ</a>
            <a href="#contact" className="hover:text-[#1a56ff] transition-colors">Contact</a>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ?
            <Link
              href="/dashboard"
              className="text-sm font-medium text-slate-700 hover:text-[#1a56ff] transition-colors px-3 py-2">
                
                Dashboard
              </Link> :

            <>
                <Link
                href="/sign-up-login-screen"
                className="text-sm font-medium text-slate-700 hover:text-[#1a56ff] transition-colors px-3 py-2">
                  
                  Sign in
                </Link>
                <Link
                href="/sign-up-login-screen?tab=register"
                className="text-sm font-semibold bg-[#1a56ff] text-white px-4 py-2 rounded-lg hover:bg-[#1648d4] transition-colors">
                  
                  Create Account
                </Link>
              </>
            }
          </div>


          <button
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu">
            
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {mobileMenuOpen &&
        <div className="md:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-1">
            <a href="#services" className="block text-sm font-medium text-slate-700 py-2.5 border-b border-slate-50" onClick={() => setMobileMenuOpen(false)}>Services</a>
            <a href="#how-it-works" className="block text-sm font-medium text-slate-700 py-2.5 border-b border-slate-50" onClick={() => setMobileMenuOpen(false)}>How it works</a>
            <a href="#faq" className="block text-sm font-medium text-slate-700 py-2.5 border-b border-slate-50" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            <a href="#contact" className="block text-sm font-medium text-slate-700 py-2.5" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            <div className="pt-3 flex flex-col gap-2">
              <Link href="/sign-up-login-screen" className="text-center text-sm font-medium text-slate-700 border border-slate-200 rounded-lg py-2.5">Sign in</Link>
              <Link href="/sign-up-login-screen?tab=register" className="text-center text-sm font-semibold bg-[#1a56ff] text-white rounded-lg py-2.5">Create Account</Link>
            </div>
          </div>
        }
      </header>
      {/* ── HERO ── */}
      <section className="pt-28 sm:pt-36 pb-20 px-4 sm:px-6 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-50 text-[#1a56ff] text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1a56ff] animate-pulse" />
              Now accepting agents across India
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold leading-[1.1] tracking-tight mb-5">
              Build a Ready Made
              <br />
              <span className="text-[#1a56ff]">Business in 24 Hours.</span>
            </h1>

            <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-8 max-w-lg">
              Become an authorized dropservice.in agent. Sell Website Development, Press Releases, and Business Compliance services — we deliver everything, you keep the profit.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              {isLoggedIn ?
              <Link
                href="/dashboard"
                className="inline-flex items-center justify-center gap-2 bg-[#1a56ff] text-white font-semibold px-6 py-3.5 rounded-xl hover:bg-[#1648d4] transition-all duration-200 text-sm shadow-lg shadow-blue-200">
                  
                  Manage Your Business
                  <ArrowRight size={16} />
                </Link> :

              <Link
                href="/sign-up-login-screen?tab=register"
                className="inline-flex items-center justify-center gap-2 bg-[#1a56ff] text-white font-semibold px-6 py-3.5 rounded-xl hover:bg-[#1648d4] transition-all duration-200 text-sm shadow-lg shadow-blue-200">
                  
                  Create Account
                  <ArrowRight size={16} />
                </Link>
              }
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-700 font-semibold px-6 py-3.5 rounded-xl hover:bg-slate-200 transition-colors text-sm">
                
                See how it works
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-5 text-sm text-slate-500">
              {['Free training included', 'Authorization certificate', '24hr delivery guarantee']?.map((item) =>
              <span key={item} className="flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                  {item}
                </span>
              )}
            </div>
          </div>

          {/* Hero visual card */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="bg-gradient-to-br from-[#0f172a] to-[#1e3a8a] rounded-3xl p-8 text-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <BadgeCheck size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-blue-300 font-medium">Authorized Agent</div>
                    <div className="text-sm font-bold">dropservice.in</div>
                  </div>
                </div>
                <div className="space-y-3 mb-6">
                  {['Website Development', 'Press Release Services', 'Business Compliance']?.map((s) =>
                  <div key={s} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
                      <CheckCircle2 size={15} className="text-emerald-400 flex-shrink-0" />
                      <span className="text-sm font-medium">{s}</span>
                    </div>
                  )}
                </div>
                <div className="border-t border-white/10 pt-5 grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-2xl font-bold">Lifetime</div>
                    <div className="text-xs text-blue-300 mt-0.5">One-time · Full access</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">15%</div>
                    <div className="text-xs text-blue-300 mt-0.5">Referral commission</div>
                  </div>
                </div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl px-4 py-3 shadow-xl border border-slate-100 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Zap size={14} className="text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs font-bold text-[#0f172a]">Delivered in 24hrs</div>
                  <div className="text-[10px] text-slate-400">Guaranteed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── STATS STRIP ── */}
      <section className="border-y border-slate-100 bg-slate-50/60 py-10 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS?.map((s) =>
          <div key={s?.label} className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-[#0f172a]">{s?.value}</div>
              <div className="text-sm text-slate-500 mt-1">{s?.label}</div>
            </div>
          )}
        </div>
      </section>
      {/* ── SERVICES ── */}
      <section id="services" className="py-20 px-4 sm:px-6 max-w-6xl mx-auto">
        <div className="mb-12">
          <p className="text-xs font-semibold text-[#1a56ff] uppercase tracking-widest mb-3">What you can sell</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Three services. Unlimited earning potential.</h2>
          <p className="text-slate-500 mt-3 max-w-lg">Pick one or sell all three. Every service is fulfilled by our expert team within 24 hours.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES?.map((svc) => {
            const SvcIcon = svc?.icon;
            return (
              <div
                key={svc?.title}
                className={`bg-gradient-to-br ${svc?.color} rounded-2xl p-7 flex flex-col justify-between min-h-[220px] ${svc?.span}`}>
                
                <div>
                  <div className="flex items-start justify-between mb-5">
                    <div className={`w-11 h-11 rounded-xl ${svc?.iconBg} flex items-center justify-center`}>
                      <SvcIcon size={20} className={svc?.iconColor} />
                    </div>
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${svc?.tagColor}`}>{svc?.tag}</span>
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${svc?.textColor}`}>{svc?.title}</h3>
                  <p className={`text-sm leading-relaxed ${svc?.descColor}`}>{svc?.desc}</p>
                </div>
                <Link
                  href="/sign-up-login-screen?tab=register"
                  className={`mt-5 inline-flex items-center gap-1 text-sm font-semibold ${svc?.textColor} hover:gap-2 transition-all`}>
                  
                  Start selling <ChevronRight size={14} />
                </Link>
              </div>);

          })}
        </div>
      </section>
      {/* ── BENEFITS BENTO ── */}
      <section className="py-20 px-4 sm:px-6 bg-slate-50/60">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs font-semibold text-[#1a56ff] uppercase tracking-widest mb-3">Everything included</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Your complete business toolkit</h2>
            <p className="text-slate-500 mt-3 max-w-lg">Everything you need to run a professional service business — from day one.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {BENEFITS?.map((b) => {
              const BenefitIcon = b?.icon;
              return (
                <div key={b?.title} className={`${b?.color} rounded-2xl p-7 flex gap-5 items-start`}>
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                    <BenefitIcon size={22} className={b?.iconColor} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#0f172a] mb-1.5">{b?.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{b?.desc}</p>
                  </div>
                </div>);

            })}
          </div>
        </div>
      </section>
      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 max-w-6xl mx-auto">
        <div className="mb-12">
          <p className="text-xs font-semibold text-[#1a56ff] uppercase tracking-widest mb-3">Simple process</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">From registration to first earning</h2>
          <p className="text-slate-500 mt-3 max-w-lg">Four steps to launch your drop-service business. Most agents earn their first commission within the first week.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HOW_IT_WORKS?.map((item, i) =>
          <div key={item?.step} className="relative">
              {i < HOW_IT_WORKS?.length - 1 &&
            <div className="hidden lg:block absolute top-6 left-full w-full h-px bg-slate-200 z-0" style={{ width: 'calc(100% - 48px)', left: '48px' }} />
            }
              <div className="relative z-10 bg-white rounded-2xl p-6 border border-slate-100 card-shadow h-full">
                <div className="text-3xl font-black text-slate-100 mb-4 leading-none">{item?.step}</div>
                <h3 className="text-base font-bold text-[#0f172a] mb-2">{item?.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item?.desc}</p>
              </div>
            </div>
          )}
        </div>
      </section>
      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 bg-slate-50/60">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs font-semibold text-[#1a56ff] uppercase tracking-widest mb-3">Real agents, real results</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Agents across India are earning</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS?.map((t) =>
            <div key={t?.name} className="bg-white border border-slate-100 rounded-2xl p-6 card-shadow-md flex flex-col gap-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)]?.map((_, i) =>
                <Star key={i} size={13} className="fill-amber-400 text-amber-400" />
                )}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed flex-1">&ldquo;{t?.text}&rdquo;</p>
                <div className="flex items-center gap-3 pt-2 border-t border-slate-50">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${t?.color}`}>
                    {t?.initials}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0f172a]">{t?.name}</div>
                    <div className="text-xs text-slate-400">{t?.city} · {t?.orders} orders</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* ── FAQ ── */}
      <section id="faq" className="py-20 px-4 sm:px-6 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs font-semibold text-[#1a56ff] uppercase tracking-widest mb-3">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Common questions</h2>
            <p className="text-slate-500 leading-relaxed mb-6">
              Everything you need to know before becoming an agent. Can&apos;t find your answer?{' '}
              <a href="#contact" className="text-[#1a56ff] font-medium hover:underline">Contact us</a>.
            </p>
            <Link
              href="/sign-up-login-screen?tab=register"
              className="inline-flex items-center gap-2 bg-[#1a56ff] text-white font-semibold px-5 py-3 rounded-xl hover:bg-[#1648d4] transition-colors text-sm">
              
              Create Account
              <ArrowRight size={15} />
            </Link>
          </div>

          <div className="space-y-3">
            {FAQS?.map((faq, i) =>
            <div key={i} className="border border-slate-100 rounded-2xl overflow-hidden">
                <button
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                
                  <span className="text-sm font-semibold text-[#0f172a]">{faq?.q}</span>
                  <ChevronRight
                  size={16}
                  className={`text-slate-400 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-90' : ''}`} />
                
                </button>
                {openFaq === i &&
              <div className="px-5 pb-4">
                    <p className="text-sm text-slate-500 leading-relaxed">{faq?.a}</p>
                  </div>
              }
              </div>
            )}
          </div>
        </div>
      </section>
      {/* ── LEADERBOARD TEASER ── */}
      <section className="py-16 px-4 sm:px-6 bg-gradient-to-br from-[#0f172a] to-[#1e3a8a]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-white">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={20} className="text-amber-400" />
              <span className="text-xs font-semibold text-blue-300 uppercase tracking-widest">Agent Leaderboard</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2">Compete. Earn. Repeat.</h2>
            <p className="text-blue-200 text-sm max-w-md">Top agents earn bonus rewards every month. See where you rank among thousands of agents nationwide.</p>
          </div>
          <Link
            href="/sign-up-login-screen?tab=register"
            className="flex-shrink-0 inline-flex items-center gap-2 bg-white text-[#0f172a] font-semibold px-6 py-3.5 rounded-xl hover:bg-blue-50 transition-colors text-sm">
            
            Join the leaderboard
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
      {/* ── CTA ── */}
      <section className="py-24 px-4 sm:px-6 max-w-6xl mx-auto text-center">
        <div className="max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
            <CheckCircle2 size={13} />
            Lifetime access · No monthly fees
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Your business starts today.
          </h2>
          <p className="text-slate-500 text-lg mb-8 max-w-md mx-auto">
            Join 2,400+ agents already building their income with dropservice.in. One-time activation fee. Lifetime access.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {isLoggedIn ?
            <Link
              href="/dashboard"
              className="inline-flex items-center justify-center gap-2 bg-[#1a56ff] text-white font-semibold px-8 py-4 rounded-xl hover:bg-[#1648d4] transition-all duration-200 text-sm shadow-lg shadow-blue-200">
                  
                  Manage Your Business
                  <ArrowRight size={16} />
                </Link> :

            <>
                <Link
                href="/sign-up-login-screen?tab=register"
                className="inline-flex items-center justify-center gap-2 bg-[#1a56ff] text-white font-semibold px-8 py-4 rounded-xl hover:bg-[#1648d4] transition-all duration-200 text-sm shadow-lg shadow-blue-200">
                  
                  Create Account
                  <ArrowRight size={16} />
                </Link>
                <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-700 font-semibold px-8 py-4 rounded-xl hover:bg-slate-200 transition-colors text-sm">
                  
                  Sign in to dashboard
                </Link>
              </>
            }
          </div>
          <p className="text-xs text-slate-400 mt-4">One-time payment · Lifetime access · Authorization certificate included</p>
        </div>
      </section>
      {/* ── CONTACT ── */}
      <section id="contact" className="py-16 px-4 sm:px-6 bg-slate-50/60">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <p className="text-xs font-semibold text-[#1a56ff] uppercase tracking-widest mb-3">Get in touch</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Contact Us</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="bg-white rounded-2xl p-6 border border-slate-100 card-shadow flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Mail size={18} className="text-[#1a56ff]" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#0f172a] mb-1">Email Support</div>
                <div className="text-sm text-slate-500">support@dropservice.in</div>
                <div className="text-xs text-slate-400 mt-1">Response within 24 hours</div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-slate-100 card-shadow flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Phone size={18} className="text-emerald-600" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#0f172a] mb-1">WhatsApp Support</div>
                <div className="text-sm text-slate-500">Available on dashboard</div>
                <div className="text-xs text-slate-400 mt-1">Mon–Sat, 10am–7pm IST</div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-slate-100 card-shadow flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                <MapPin size={18} className="text-purple-600" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#0f172a] mb-1">Registered Office</div>
                <div className="text-sm text-slate-500">Kanglei Sannathong Pvt. Limited</div>
                <div className="text-xs text-slate-400 mt-1">India</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── FOOTER ── */}
      <footer className="bg-[#0f172a] text-slate-400 pt-16 pb-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Top row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 pb-12 border-b border-white/10">
            {/* Brand column */}
            <div className="col-span-2 sm:col-span-3 lg:col-span-1">
              <span className="text-[17px] font-bold tracking-tight select-none">
                <span className="text-white">dropservice</span>
                <span className="text-[#1a56ff]">.in</span>
              </span>
              <p className="text-sm text-slate-500 mt-3 leading-relaxed max-w-[200px]">
                India&apos;s trusted drop-service platform for aspiring entrepreneurs.
              </p>
              <div className="mt-5 flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2.5 w-fit">
                <TrendingUp size={14} className="text-emerald-400" />
                <span className="text-xs text-slate-300 font-medium">2,400+ Active Agents</span>
              </div>
            </div>

            {/* Link columns */}
            {Object.entries(FOOTER_LINKS)?.map(([category, links]) =>
            <div key={category}>
                <div className="text-xs font-semibold text-white uppercase tracking-widest mb-4">{category}</div>
                <ul className="space-y-2.5">
                  {links?.map((link) =>
                <li key={link?.label}>
                      <Link
                    href={link?.href}
                    className="text-sm text-slate-400 hover:text-white transition-colors">
                    
                        {link?.label}
                      </Link>
                    </li>
                )}
                </ul>
              </div>
            )}
          </div>

          {/* Bottom row */}
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div className="text-center sm:text-left">
              <p className="text-xs text-slate-500">
                © {CURRENT_YEAR} dropservice.in · All rights reserved
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Made with ❤️ | A product of{' '}
                <span className="text-slate-400 font-medium">Kanglei Sannathong Pvt. Limited</span>
              </p>
            </div>
          </div>
        </div>
      </footer>
      {/* DMCA Badge Helper Script */}
      {/* eslint-disable-next-line @next/next/no-sync-scripts */}
      <Script
        strategy="afterInteractive"
        src="https://images.dmca.com/Badges/DMCABadgeHelper.min.js" />
      
    </div>);

}